import { FC, useMemo, useState } from 'react';
import { html } from 'lit/static-html.js';
import { ColumnDef } from '@tanstack/react-table';
import {
  Button,
  DataTable,
  Icon,
  PageSection,
  Search,
  Spacer,
  Stack,
  TextBody,
  TextCaption,
  TextTitle,
  Tooltip,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';

import { formatFileSize, UNKNOWN_VALUE } from '../../utils';

/** A row: either one file, or one custom asset folder presented as a file. */
export type FileListItem = {
  name: string;
  path: string;
  size?: number | null;
  mimeType?: string | null;
  /** True while an upload placeholder for this row is in flight. */
  loading?: boolean;
};

export type FileRowAction = 'edit' | 'download' | 'delete';

export type FileListSectionProps = {
  /** Prefix for every `test-id` this section renders, e.g. `ps-actions`. */
  testIdPrefix: string;
  title: string;
  tooltip: string;
  items: FileListItem[];
  /** Viewer role — hides upload and the mutating row actions entirely. */
  readOnly?: boolean;
  /** Upload is possible in principle but not right now, e.g. no draft version. */
  uploadDisabled?: boolean;
  uploadLabel: string;
  rowActions: FileRowAction[];
  /** Hides the edit button on rows the caller cannot edit; every row is editable by default. */
  canEdit?: (item: FileListItem) => boolean;
  showSearch?: boolean;
  showDownloadAll?: boolean;
  isLoading?: boolean;
  error?: string;
  emptyLabel: string;
  onUploadClick: () => void;
  onDownloadAll?: () => void;
  onEdit?: (item: FileListItem) => void;
  onDownload?: (item: FileListItem) => void;
  onDelete?: (item: FileListItem) => void;
};

/**
 * One card listing one category of solution files. Used for Configuration, Actions and
 * custom Assets — it deliberately knows nothing about file types, endpoints or conflict
 * resolution, so all three callers share it.
 *
 * Cells are `lit` templates rather than JSX because the table is a web component; this
 * mirrors how `actions.tsx` built its columns.
 */
export const FileListSection: FC<FileListSectionProps> = ({
  testIdPrefix,
  title,
  tooltip,
  items,
  readOnly,
  uploadDisabled,
  uploadLabel,
  rowActions,
  canEdit,
  showSearch = true,
  showDownloadAll = true,
  isLoading,
  error,
  emptyLabel,
  onUploadClick,
  onDownloadAll,
  onEdit,
  onDownload,
  onDelete,
}) => {
  const [t] = useTranslation();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => item.name.toLowerCase().includes(needle));
  }, [items, query]);

  // Only the actions the caller asked for, and never the mutating ones for a viewer.
  const visibleActions = useMemo(
    () =>
      rowActions.filter((action) => (readOnly ? action === 'download' : true)),
    [rowActions, readOnly]
  );

  const columns = useMemo<ColumnDef<FileListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: () =>
          t('pro-studio/actions:file_list.name', { defaultValue: 'NAME' }),
        cell: ({ row }) =>
          html`<ps-text-subtitle size="small">
            ${row.original.name}
          </ps-text-subtitle>`,
      },
      {
        id: 'size',
        header: () =>
          t('pro-studio/actions:file_list.size', { defaultValue: 'SIZE' }),
        cell: ({ row }) =>
          html`<ps-text-body size="small">
            ${formatFileSize(row.original.size)}
          </ps-text-body>`,
      },
      {
        id: 'mimeType',
        header: () =>
          t('pro-studio/actions:file_list.type', { defaultValue: 'TYPE' }),
        cell: ({ row }) =>
          html`<ps-text-body size="small">
            ${row.original.mimeType || UNKNOWN_VALUE}
          </ps-text-body>`,
      },
      {
        id: 'actions',
        header: () =>
          html`<ps-text-body style="opacity: 0">Actions</ps-text-body>`,
        cell: ({ row }) => html`
          <ps-stack
            alignItems="center"
            justifyContent="end"
            gutter="xsmall"
            nowrap
          >
            ${visibleActions.includes('edit') &&
            (canEdit?.(row.original) ?? true)
              ? html`<ps-icon-button
                  name="edit"
                  size="small"
                  ?disabled=${row.original.loading}
                  @click=${(event: Event) => {
                    event.stopPropagation();
                    onEdit?.(row.original);
                  }}
                ></ps-icon-button>`
              : null}
            ${visibleActions.includes('download')
              ? html`<ps-icon-button
                  name="download"
                  size="small"
                  ?disabled=${row.original.loading}
                  @click=${(event: Event) => {
                    event.stopPropagation();
                    onDownload?.(row.original);
                  }}
                ></ps-icon-button>`
              : null}
            ${visibleActions.includes('delete')
              ? html`<ps-icon-button
                  name="delete"
                  size="small"
                  ?disabled=${row.original.loading}
                  @click=${(event: Event) => {
                    event.stopPropagation();
                    onDelete?.(row.original);
                  }}
                ></ps-icon-button>`
              : null}
          </ps-stack>
        `,
      },
    ],
    [t, visibleActions, canEdit, onEdit, onDownload, onDelete]
  );

  return (
    <PageSection test-id={`${testIdPrefix}-section`} size="small">
      {/* Title hard left, everything else grouped hard right. */}
      <Stack alignItems="center" justifyContent="space-between" nowrap>
        <Stack alignItems="center" gutter="2xsmall" nowrap>
          <TextTitle test-id={`${testIdPrefix}-title`} size="xsmall">
            {title}
          </TextTitle>
          <Tooltip test-id={`${testIdPrefix}-tooltip`} placement="right">
            <Icon name="info" color="gray" size="small" />
            <TextCaption variant="primary" slot="content">
              {tooltip}
            </TextCaption>
          </Tooltip>
        </Stack>
        {/*
          Search, `Download all` and the upload button form one group on the right. That
          only works because `Search` has a fixed width — an `Input` here stretches and
          squeezes the buttons until their labels wrap.
        */}
        <Stack alignItems="center" gutter="small" nowrap>
          {showSearch && (
            // Same declaration as the Solutions search on the home page: `Search` (which
            // carries the magnifier itself) at a fixed width, rather than an `Input` that
            // stretches to fill the row.
            <Search
              test-id={`${testIdPrefix}-search`}
              size="large"
              name={`${testIdPrefix}-search`}
              style={{ width: 319 }}
              placeholder={
                t('pro-studio/actions:file_list.search', {
                  defaultValue: 'Search',
                }) || ''
              }
              value={query}
              onInput={(event: { target?: { value?: unknown } }) =>
                setQuery(String(event.target?.value ?? ''))
              }
            />
          )}
          {showDownloadAll && (
            <Button
              test-id={`${testIdPrefix}-download-all`}
              variant="ghost"
              size="large"
              disabled={!items.length}
              onClick={() => onDownloadAll?.()}
            >
              <Icon name="download" slot="prefix" />
              {t('pro-studio/actions:file_list.download_all', {
                defaultValue: 'Download all',
              })}
            </Button>
          )}
          {!readOnly && (
            <Button
              test-id={`${testIdPrefix}-upload`}
              size="large"
              disabled={uploadDisabled}
              onClick={onUploadClick}
            >
              <Icon name="upload" slot="prefix" />
              {uploadLabel}
            </Button>
          )}
        </Stack>
      </Stack>
      <Spacer size="large" />
      {error ? (
        <TextBody test-id={`${testIdPrefix}-error`} size="small">
          {error}
        </TextBody>
      ) : filtered.length ? (
        <DataTable
          test-id={`${testIdPrefix}-table`}
          size="small"
          columns={columns}
          data={filtered}
        />
      ) : (
        <TextBody test-id={`${testIdPrefix}-empty`} size="small">
          {isLoading ? '' : emptyLabel}
        </TextBody>
      )}
    </PageSection>
  );
};
